import { BackButton, PageContainer } from "@client/components/ui";
import { captureClientException } from "@client/lib/sentry";
import { useTriggerAuth } from "@client/lib/trigger-provider";
import { trpc } from "@client/lib/trpc-client";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useRealtimeRun } from "@trigger.dev/react-hooks";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { JobProgressCard } from "../-components/job-progress-card";
import { JobQuickActions } from "../-components/job-quick-actions";
import { JobStatusHeader } from "../-components/job-status-header";
import { LlmsFileCard } from "../-components/llms-file-card";

export const Route = createFileRoute("/(protected)/generation/$jobId")({
	component: GenerationStatusPage,
});

function GenerationStatusPage() {
	const { jobId } = Route.useParams();
	const navigate = useNavigate();
	const [elapsedTime, setElapsedTime] = useState(0);
	const [previousStatus, setPreviousStatus] = useState<string | null>(null);
	const [tokenReady, setTokenReady] = useState(false);
	const { setAccessToken } = useTriggerAuth();

	// Get initial job data from our database (includes triggerRunId)
	const { data: jobStatus, error: jobError } =
		trpc.generation.getStatus.useQuery(
			{ jobId },
			{
				// Reduce polling frequency since realtime will handle most updates
				refetchInterval: (query) => {
					const data = query.state.data;
					// Only poll if we don't have realtime updates and job is still processing
					if (
						!data?.publicAccessToken &&
						data &&
						["processing", "queued"].includes(data.status)
					) {
						return 30000; // Fallback polling every 30 seconds if no realtime
					}
					return false; // Stop polling when we have realtime or job is complete
				},
				refetchIntervalInBackground: false,
			},
		);

	// Mutation to regenerate token when there's a mismatch
	const regenerateTokenMutation = trpc.generation.regenerateToken.useMutation({
		onSuccess: (data) => {
			// Set the new token
			if (data.publicAccessToken) {
				setAccessToken(data.publicAccessToken);
			}

			// Refetch job status to get updated token
			window.location.reload(); // Simple approach to refresh everything
		},
		onError: (error) => {
			toast.error("Failed to regenerate access token");
			console.error("Failed to regenerate access token", error);
		},
	});

	// Set the access token when we receive it from the job status
	useEffect(() => {
		if (
			jobStatus?.publicAccessToken &&
			jobStatus.publicAccessToken.trim() !== ""
		) {
			// Add a small delay to ensure the provider is ready
			setTimeout(() => {
				setAccessToken(jobStatus.publicAccessToken);
				setTokenReady(true);
			}, 100);
		} else if (
			jobStatus?.publicAccessToken === null ||
			jobStatus?.publicAccessToken === ""
		) {
			// Explicitly clear token if we get null or empty string
			setAccessToken(null);
			setTokenReady(false);
		}
	}, [jobStatus?.publicAccessToken, setAccessToken]);

	// Clear token when component unmounts
	useEffect(() => {
		return () => {
			setAccessToken(null);
		};
	}, [setAccessToken]);

	// Use realtime updates for live progress tracking
	const shouldEnableRealtime = !!(
		tokenReady &&
		jobStatus?.firecrawlJobId &&
		jobStatus?.publicAccessToken &&
		jobStatus?.publicAccessToken.trim() !== ""
	);

	// Get tRPC utils for query invalidation
	const trpcUtils = trpc.useUtils();

	// Track if we've already shown completion notification to prevent infinite loop
	const [completionNotified, setCompletionNotified] = useState(false);

	// Use standard hook with direct accessToken option as fallback
	// According to Trigger.dev docs, we can provide accessToken directly in options
	const { run: realtimeRun, error: realtimeError } = useRealtimeRun(
		jobStatus?.firecrawlJobId ?? "",
		{
			enabled: shouldEnableRealtime && !!jobStatus?.firecrawlJobId,
			// Provide accessToken directly in options as documented fallback
			...(jobStatus?.publicAccessToken && {
				accessToken: jobStatus.publicAccessToken,
			}),
			onComplete: async (run) => {
				// Only show notification once
				if (completionNotified) return;

				setCompletionNotified(true);

				if (run?.status === "COMPLETED") {
					toast.success("LLMs.txt generation completed successfully!");
				} else if (run?.status === "FAILED") {
					toast.error("Generation failed. Please try again.");
				}

				// Invalidate queries to refresh data instead of full page reload
				await Promise.all([
					trpcUtils.generation.getStatus.invalidate({ jobId }),
					trpcUtils.sites.invalidate(), // Refresh sites data
					trpcUtils.credits.invalidate(), // Refresh credit balance after generation
				]);
			},
		},
	);

	// Auto-regenerate token if there's a permission error or missing access token
	useEffect(() => {
		if (
			realtimeError?.message?.includes("missing required permissions") ||
			realtimeError?.message?.includes("Missing accessToken")
		) {
			if (!regenerateTokenMutation.isPending) {
				regenerateTokenMutation.mutate({ jobId });
			}
		}
	}, [realtimeError, jobId, regenerateTokenMutation]);

	// Get site details for context (if siteId is available in job data)
	const { data: siteDetails } = trpc.sites.getById.useQuery(
		{ id: jobStatus?.siteId || "" },
		{ enabled: !!jobStatus?.siteId },
	);

	// Report errors to Sentry
	useEffect(() => {
		if (jobError) {
			captureClientException(new Error(jobError.message), {
				tags: {
					operation: "getJobStatus",
					component: "GenerationStatusPage",
				},
				extra: {
					jobId,
					trpcError: jobError,
				},
			});
		}

		if (realtimeError) {
			captureClientException(new Error(realtimeError.message), {
				tags: {
					operation: "realtimeRun",
					component: "GenerationStatusPage",
				},
				extra: {
					jobId,
					triggerRunId: jobStatus?.firecrawlJobId,
					hasToken: !!jobStatus?.publicAccessToken,
					realtimeError,
				},
			});
		}
	}, [
		jobError,
		realtimeError,
		jobId,
		jobStatus?.firecrawlJobId,
		jobStatus?.publicAccessToken,
	]);

	// Normalize SQLite date strings to proper UTC ISO format
	// SQLite CURRENT_TIMESTAMP stores UTC without 'Z' suffix (e.g., "2026-02-17 19:16:11")
	// JavaScript parses this as local time, causing timezone mismatch
	const parseAsUTC = useCallback(
		(dateStr: string | Date | undefined | null): number | null => {
			if (!dateStr) return null;
			if (dateStr instanceof Date) return dateStr.getTime();
			const str = String(dateStr);
			// If it's already ISO with T and timezone info, parse directly
			if (str.includes("T")) return new Date(str).getTime();
			// SQLite format: "2026-02-17 19:16:11" — append Z to treat as UTC
			return new Date(`${str.replace(" ", "T")}Z`).getTime();
		},
		[],
	);

	// Update elapsed time every second based on actual job creation time
	// Stop the timer when job reaches a terminal state (completed/failed)
	useEffect(() => {
		// Use realtime run createdAt as fallback if database doesn't have it yet
		const createdAt = jobStatus?.createdAt || realtimeRun?.createdAt;
		const jobCreatedAt = parseAsUTC(createdAt);
		if (!jobCreatedAt || Number.isNaN(jobCreatedAt)) return;

		// Check if job is in a terminal state
		const status = realtimeRun?.status
			? String(realtimeRun.status).toLowerCase()
			: jobStatus?.status;
		const isTerminal = status === "completed" || status === "failed";

		if (isTerminal) {
			// Use completedAt for final elapsed time, or current time as fallback
			const endTime = parseAsUTC(jobStatus?.completedAt) || Date.now();
			setElapsedTime(Math.max(0, Math.floor((endTime - jobCreatedAt) / 1000)));
			return; // Don't start interval — timer is frozen
		}

		const timer = setInterval(() => {
			const now = Date.now();
			setElapsedTime(Math.max(0, Math.floor((now - jobCreatedAt) / 1000)));
		}, 1000);

		return () => clearInterval(timer);
	}, [
		jobStatus?.createdAt,
		jobStatus?.completedAt,
		jobStatus?.status,
		realtimeRun?.createdAt,
		realtimeRun?.status,
		parseAsUTC,
	]);

	// Handle status changes from both sources
	const currentStatus = realtimeRun?.status
		? String(realtimeRun.status)
		: jobStatus?.status;
	const currentError = realtimeError?.message || jobError?.message;

	// Handle job status changes - only show toast when status actually changes
	useEffect(() => {
		if (currentStatus && previousStatus !== null) {
			// Only show toast if status changed from previous state
			if (currentStatus !== previousStatus) {
				if (currentStatus === "COMPLETED" || currentStatus === "completed") {
					toast.success("LLMs.txt generation completed successfully!");
				} else if (currentStatus === "FAILED" || currentStatus === "failed") {
					toast.error("Generation failed. Please try again.");
				}
			}
		}

		// Update previous status for next comparison
		if (currentStatus) {
			setPreviousStatus(String(currentStatus));
		}
	}, [currentStatus, previousStatus]);

	const getStatusMessage = () => {
		if (currentError) {
			// More specific error messages
			if (currentError.includes("NOT_FOUND")) {
				return "Job not found or expired";
			}
			if (currentError.includes("FORBIDDEN")) {
				return "Access denied to this job";
			}
			return `Error: ${currentError}`;
		}
		if (!jobStatus && !realtimeRun)
			return "Connecting to generation service...";

		// Map Trigger.dev statuses to our internal statuses
		const status = realtimeRun?.status
			? String(realtimeRun.status).toLowerCase()
			: jobStatus?.status;

		switch (status) {
			case "completed":
				return "Generation completed successfully!";
			case "failed":
				return (
					jobStatus?.error ||
					(typeof realtimeRun?.error === "string"
						? realtimeRun.error
						: realtimeRun?.error?.message) ||
					"Generation failed"
				);
			case "executing":
			case "processing":
				return "Crawling website and generating content...";
			case "queued":
			case "waiting":
				return "Job queued, waiting to start...";
			case "resting":
				return "Preparing to start generation...";
			default:
				return "Processing your request...";
		}
	};

	const getProgressValue = () => {
		if (!jobStatus && !realtimeRun) return 0;

		const status = realtimeRun?.status
			? String(realtimeRun.status).toLowerCase()
			: jobStatus?.status;

		// ALWAYS return 100 for completed status, regardless of metadata
		switch (status) {
			case "completed":
				return 100;
			case "failed":
				return 0;
			case "executing":
			case "processing": {
				// Use realtime metadata progress directly, as Firecrawl services now report granular progress
				if (realtimeRun?.metadata?.progress !== undefined) {
					const metadataProgress = Number(realtimeRun.metadata.progress);
					if (
						!Number.isNaN(metadataProgress) &&
						metadataProgress >= 0 &&
						metadataProgress <= 100
					) {
						return metadataProgress;
					}
				}

				// Check original DB progress
				if (jobStatus?.progress && jobStatus.progress !== "0") {
					const parsed = Number.parseInt(jobStatus.progress, 10);
					if (!Number.isNaN(parsed) && parsed >= 0 && parsed <= 100) {
						return parsed;
					}
				}

				return 10; // Default when starting
			}
			case "queued":
			case "waiting":
				return 5;
			case "resting":
				return 10;
			default:
				return 10;
		}
	};

	return (
		<PageContainer maxWidth="full">
			{/* Navigation */}
			<BackButton
				to={jobStatus?.siteId ? `/sites/${jobStatus.siteId}` : "/jobs"}
				label={jobStatus?.siteId ? "Back to site" : "Back to jobs"}
			/>

			{/* Header Section */}
			<JobStatusHeader
				status={currentStatus}
				error={currentError}
				siteDetails={siteDetails}
				statusMessage={getStatusMessage()}
			/>

			{/* Main Progress Card */}
			<JobProgressCard
				progressValue={getProgressValue()}
				status={currentStatus}
				error={currentError}
				jobError={jobStatus?.error ?? undefined}
				fileTextUrl={jobStatus?.fileTextUrl ?? undefined}
				fileFullTextUrl={jobStatus?.fullTextUrl ?? undefined}
				siteId={jobStatus?.siteId ?? undefined}
				elapsedTime={elapsedTime}
				processedUrls={
					realtimeRun?.metadata?.urlsFound
						? Number(realtimeRun.metadata.urlsFound)
						: realtimeRun?.metadata?.urlsProcessed
							? Number(realtimeRun.metadata.urlsProcessed)
							: jobStatus?.processedUrls?.length || 0
				}
				totalUrls={
					realtimeRun?.metadata?.totalUrls
						? Number(realtimeRun.metadata.totalUrls)
						: undefined
				}
				creditsUsed={
					realtimeRun?.metadata?.creditsUsed
						? Number(realtimeRun.metadata.creditsUsed)
						: jobStatus?.creditsUsed || 0
				}
				realtimeConnected={shouldEnableRealtime && !!realtimeRun}
			/>

			{/* Generated Files — shown when complete */}
			{(currentStatus === "completed" || currentStatus === "COMPLETED") &&
				jobStatus?.siteId &&
				jobStatus?.fileTextUrl && (
					<div className="mb-8">
						<LlmsFileCard
							siteId={jobStatus.siteId}
							fileTextUrl={jobStatus.fileTextUrl}
							onGenerate={() =>
								navigate({
									to: "/sites/$siteId",
									params: { siteId: jobStatus.siteId! },
								})
							}
						/>
					</div>
				)}

			{/* Quick Actions */}
			<JobQuickActions
				siteId={jobStatus?.siteId ?? undefined}
				jobId={jobId}
				jobStatus={currentStatus}
			/>

			{/* Debug info in development */}
			{process.env.NODE_ENV === "development" && (
				<div className="mt-4 rounded border-2 border-gray-300 bg-gray-50 p-4 text-xs dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
					<p className="mb-2 font-semibold text-base dark:text-gray-200">
						🔍 Debug Info
					</p>

					<div className="mb-3 space-y-1">
						<p className="font-semibold dark:text-gray-200">Database State:</p>
						<p className="dark:text-gray-300">
							• Job Status: {jobStatus?.status}
						</p>
						<p className="dark:text-gray-300">
							• Trigger Run ID: {jobStatus?.firecrawlJobId}
						</p>
						<p className="dark:text-gray-300">
							• DB Progress: {jobStatus?.progress || "0"}%
						</p>
						<p className="dark:text-gray-300">
							• DB URLs Processed: {jobStatus?.processedUrls?.length || 0}
						</p>
						<p className="dark:text-gray-300">
							• DB Credits Used: {jobStatus?.creditsUsed || 0}
						</p>
					</div>

					<div className="mb-3 space-y-1">
						<p className="font-semibold dark:text-gray-200">
							Realtime WebSocket State:
						</p>
						<p className="dark:text-gray-300">
							• Status:{" "}
							{realtimeRun?.status
								? String(realtimeRun.status)
								: "Not connected"}
						</p>
						<p className="dark:text-gray-300">
							• Connection: {shouldEnableRealtime ? "🟢 Active" : "🔴 Inactive"}
						</p>
						<p className="dark:text-gray-300">
							• Token Ready: {tokenReady ? "Yes" : "No"}
						</p>
						<p className="dark:text-gray-300">
							• Has Public Token: {jobStatus?.publicAccessToken ? "Yes" : "No"}
						</p>
						<p className="dark:text-gray-300">
							• Token Length: {jobStatus?.publicAccessToken?.length || 0}
						</p>
					</div>

					{realtimeRun?.metadata && (
						<div className="mb-3 space-y-1 rounded border border-green-300 bg-green-50 p-2 dark:border-green-900 dark:bg-green-950/30">
							<p className="font-semibold dark:text-green-200">
								📊 Live Metadata (WebSocket):
							</p>
							<p className="dark:text-green-300">
								• Progress: {String(realtimeRun.metadata.progress ?? "N/A")}%
							</p>
							<p className="dark:text-green-300">
								• URLs Found: {String(realtimeRun.metadata.urlsFound ?? "N/A")}
							</p>
							<p className="dark:text-green-300">
								• URLs Processed:{" "}
								{String(realtimeRun.metadata.urlsProcessed ?? "N/A")}
							</p>
							<p className="dark:text-green-300">
								• Credits Used:{" "}
								{String(realtimeRun.metadata.creditsUsed ?? "N/A")}
							</p>
							<p className="dark:text-green-300">
								• Current Phase: {String(realtimeRun.metadata.phase ?? "N/A")}
							</p>
							<p className="dark:text-green-300">
								• Status Message:{" "}
								{String(realtimeRun.metadata.statusMessage ?? "N/A")}
							</p>
						</div>
					)}

					<div className="mb-3 space-y-1">
						<p className="font-semibold dark:text-gray-200">Errors:</p>
						<p className="dark:text-gray-300">
							• Job Error: {jobError?.message || "None"}
						</p>
						<p className="dark:text-gray-300">
							• Realtime Error: {realtimeError?.message || "None"}
						</p>
					</div>

					{realtimeError && (
						<div className="mt-2 rounded border border-red-300 bg-red-100 p-2 dark:border-red-900 dark:bg-red-950/30">
							<p className="font-semibold dark:text-red-200">
								<strong>Realtime Error Details:</strong>
							</p>
							<p className="dark:text-red-300">
								Message: {realtimeError.message}
							</p>
							<p className="dark:text-red-300">Type: {typeof realtimeError}</p>
							{realtimeError.message.includes(
								"missing required permissions",
							) && (
								<button
									type="button"
									onClick={() => regenerateTokenMutation.mutate({ jobId })}
									disabled={regenerateTokenMutation.isPending}
									className="mt-2 rounded bg-blue-500 px-3 py-1 text-white text-xs hover:bg-blue-600 disabled:opacity-50 dark:bg-blue-600 dark:hover:bg-blue-700"
								>
									{regenerateTokenMutation.isPending
										? "Regenerating..."
										: "Regenerate Token"}
								</button>
							)}
						</div>
					)}

					{jobStatus?.firecrawlJobId && jobStatus?.publicAccessToken && (
						<div className="mt-2">
							<button
								type="button"
								onClick={() => regenerateTokenMutation.mutate({ jobId })}
								disabled={regenerateTokenMutation.isPending}
								className="rounded bg-gray-500 px-3 py-1 text-white text-xs hover:bg-gray-600 disabled:opacity-50 dark:bg-gray-600 dark:hover:bg-gray-700"
							>
								{regenerateTokenMutation.isPending
									? "Regenerating..."
									: "Manual Token Regenerate"}
							</button>
						</div>
					)}
				</div>
			)}
		</PageContainer>
	);
}
